import {
  credentialReplacer,
  isCredentialKey,
} from "@redotech/credential-scrub";
import * as z from "zod";

export const developerLogKindSchema = z.enum([
  "api_request",
  "webhook_delivery",
  "function_run",
]);
export type DeveloperLogKind = z.infer<typeof developerLogKindSchema>;

/** The `api_version` of a public MCP tool call; its `endpoint` is the tool. */
export const MCP_API_VERSION = "mcp";

export const developerLogOutcomeSchema = z.enum(["success", "failure"]);
export type DeveloperLogOutcome = z.infer<typeof developerLogOutcomeSchema>;

export const developerLogBodyStatusSchema = z.enum([
  "available",
  "failed",
  "none",
]);
export type DeveloperLogBodyStatus = z.infer<
  typeof developerLogBodyStatusSchema
>;

/**
 * One S3 metadata line and one ClickHouse row; snake_case so JSONEachRow maps
 * onto the columns as-is. Kind-specific fields are empty for other kinds.
 */
/** Both form S3 keys, so a malformed or forged value can't reach another path. */
const TEAM_ID = /^[0-9a-f]{24}$/;
const ENTRY_ID = /^[A-Za-z0-9_-]{1,64}$/;

export const developerLogEntrySchema = z.object({
  team_id: z.string().regex(TEAM_ID),
  kind: developerLogKindSchema,
  id: z.string().regex(ENTRY_ID),
  /** ISO-8601 UTC instant when the request, attempt or run started. */
  ts: z.string(),
  duration_ms: z.number().int().nonnegative(),
  outcome: developerLogOutcomeSchema,
  error_codes: z.array(z.string()),
  /**
   * Left empty, like `idempotency_key`: free text can hold shopper PII, which
   * erasure can't reach in this year-long file, so it goes in the 30-day body.
   */
  error_message: z.string(),
  related_ids: z.array(z.string()),
  body_status: developerLogBodyStatusSchema,
  status_code: z.number().int().nullable(),
  method: z.string(),
  endpoint: z.string(),
  api_version: z.string(),
  token_id: z.string(),
  token_suffix: z.string(),
  idempotency_key: z.string(),
  event_id: z.string(),
  topic: z.string(),
  endpoint_id: z.string(),
  url: z.string(),
  attempt: z.number().int().nonnegative(),
  artifact_id: z.string(),
  contract_id: z.string(),
  target_id: z.string(),
  source_kind: z.string(),
  source_id: z.string(),
  input_selector: z.array(z.string()),
  /**
   * The signed-in Redo user behind an MCP tool call; API tokens have none.
   * Defaulted so the consumer still reads messages from producers deployed
   * before the field existed.
   */
  user_id: z.string().default(""),
});
export type DeveloperLogEntry = z.infer<typeof developerLogEntrySchema>;

const EMPTY_KIND_FIELDS = {
  error_codes: [],
  error_message: "",
  related_ids: [],
  status_code: null,
  method: "",
  endpoint: "",
  api_version: "",
  token_id: "",
  token_suffix: "",
  idempotency_key: "",
  event_id: "",
  topic: "",
  endpoint_id: "",
  url: "",
  attempt: 0,
  artifact_id: "",
  contract_id: "",
  target_id: "",
  source_kind: "",
  source_id: "",
  input_selector: [],
  user_id: "",
} satisfies Partial<DeveloperLogEntry>;

type RequiredEntryFields = Pick<
  DeveloperLogEntry,
  "team_id" | "kind" | "id" | "ts" | "duration_ms" | "outcome"
>;

/** Fills every field a producer didn't set with its empty value. */
export function developerLogEntry(
  fields: RequiredEntryFields & Partial<DeveloperLogEntry>,
): DeveloperLogEntry {
  return { ...EMPTY_KIND_FIELDS, body_status: "none", ...fields };
}

/** The 30-day body object. Values are JSON as received or returned. */
export const developerLogBodySchema = z.object({
  input: z.json().optional(),
  output: z.json().optional(),
  request_headers: z.record(z.string(), z.string()).optional(),
  response_headers: z.record(z.string(), z.string()).optional(),
  ip: z.string().optional(),
  user_agent: z.string().optional(),
  console_output: z.string().optional(),
  error_message: z.string().optional(),
  idempotency_key: z.string().optional(),
  truncated: z.boolean(),
});
export type DeveloperLogBody = z.infer<typeof developerLogBodySchema>;

/** A body payload serialized once, with credentials already redacted. */
export interface RedactedJson {
  readonly json: string;
}

/**
 * Payload keys that hold an HTTP credential or session, beyond what
 * {@link isCredentialKey} matches: a caller echoing its own headers into a body.
 */
const HTTP_CREDENTIAL_KEY_PATTERNS: RegExp[] = [
  /authori[sz]ation/i,
  /cookie/i,
  /session[-_]?id/i,
  /csrf|xsrf/i,
];

export function isLoggedCredentialKey(key: string): boolean {
  return (
    isCredentialKey(key) ||
    HTTP_CREDENTIAL_KEY_PATTERNS.some((pattern) => pattern.test(key))
  );
}

/**
 * Serializes in one pass; bigints become strings, unserializable values drop,
 * and strings under credential-named keys become `REDACTED_CREDENTIAL`, so a
 * logs-read token never reads a credential. Every body payload goes through it.
 */
export function toRedactedJson(value: unknown): RedactedJson | undefined {
  if (value === undefined) {
    return undefined;
  }
  try {
    const redact = credentialReplacer(isLoggedCredentialKey);
    const json = JSON.stringify(
      value,
      function (this: unknown, key: string, item: unknown) {
        return redact.call(
          this,
          key,
          typeof item === "bigint" ? item.toString() : item,
        );
      },
    );
    return json === undefined ? undefined : { json };
  } catch {
    return undefined;
  }
}
